import { Fragment, useCallback, useEffect, useRef, useState } from 'react';

import type { UiAttemptRun, UiRunsPage } from '@nightshift/ui-types';

import { Empty, Kicker, RefreshError, Spinner, StateChip, VendorMark } from '../components/primitives.tsx';
import { TranscriptSummary, useTranscriptSummary } from '../components/transcript-summary.tsx';
import { api } from '../live/api.ts';
import { useLive } from '../live/context.tsx';
import { absoluteTime, clockStampSeconds, compactDuration, plural, tokens, usd } from '../lib/format.ts';
import { STATE_ORDER } from '../lib/palette.ts';
import { extractReadableLines, missingTranscriptNote, type TranscriptExpiry } from '../lib/transcript.ts';
import { createLatestRequest, useDebouncedValue } from '../lib/latest-request.ts';
import {
  RUN_HARNESSES,
  RUN_STATUSES,
  runsEmptyCopy,
  runsFilterActive,
  runsFilterFrom,
  statusStyle,
  type RunsFilter,
} from '../lib/runs.ts';
import type { ViewProps } from './board.tsx';
import './sheet.css';
import './runs.css';

const PAGE = 100;

/**
 * The run ledger — every execution attempt Nightshift has recorded.
 *
 * One row per `execution_attempts` row, newest first, filterable by ticket, state,
 * status and harness. A retry is its own row. A rate limit is information, not a
 * red failure.
 */
export function RunsView({
  onOpenTicket,
  filter,
  onChangeFilter,
  selectedRun,
  selectedRunLane = 'attempt',
  onSelectRun,
}: ViewProps & {
  filter: RunsFilter;
  onChangeFilter: (next: RunsFilter) => void;
  selectedRun: number | null;
  selectedRunLane?: 'attempt' | 'sidecar';
  onSelectRun: (id: number | null, lane?: 'attempt' | 'sidecar') => void;
}) {
  const live = useLive();
  const [page, setPage] = useState<UiRunsPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loadedAt, setLoadedAt] = useState<number | null>(null);
  const [latest] = useState(createLatestRequest);
  const parsed = runsFilterFrom(filter);

  const projectSlug = live.projectSlug;
  const revision = live.snapshot?.revision ?? 0;
  const ticket = useDebouncedValue(parsed.ticket, 250);

  /*
   * The offset belongs to the filter it was paged under. Resetting it in an effect after a
   * filter change fetched the new filter at the old offset first (often an empty page), then
   * again at zero; deriving it means a new filter is asked for at zero in the same render.
   */
  const filterKey = JSON.stringify([
    projectSlug,
    ticket,
    parsed.state,
    parsed.status,
    parsed.harness,
    parsed.outcome,
  ]);
  const [paging, setPaging] = useState({ filterKey, offset: 0 });
  const offset = paging.filterKey === filterKey ? paging.offset : 0;
  const setOffset = (next: number) => setPaging({ filterKey, offset: next });

  const load = useCallback(async () => {
    const request = latest.begin();
    setBusy(true);
    try {
      const next = await api.runs({
        project: projectSlug,
        ticket: ticket ?? undefined,
        state: parsed.state ?? undefined,
        status: parsed.status ?? undefined,
        harness: parsed.harness ?? undefined,
        outcome: parsed.outcome ?? undefined,
        limit: PAGE,
        offset,
      });
      if (!latest.isCurrent(request)) return;
      setPage(next);
      setLoadedAt(Date.now());
      setError(null);
    } catch (caught) {
      if (!latest.isCurrent(request)) return;
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      if (latest.isCurrent(request)) setBusy(false);
    }
  }, [
    latest,
    projectSlug,
    ticket,
    parsed.state,
    parsed.status,
    parsed.harness,
    parsed.outcome,
    offset,
  ]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  useEffect(() => () => latest.cancel(), [latest]);

  const showMachine = true;
  const filterActive = runsFilterActive(parsed);
  const empty = runsEmptyCopy({ filterActive, projectSlug });

  const patch = (next: Partial<RunsFilter>) => onChangeFilter(runsFilterFrom({ ...parsed, ...next }));

  if (error && !page) {
    return (
      <Empty
        title="The run ledger could not be read."
        body={error}
        action={
          <button type="button" className="btn" onClick={() => void load()}>
            Try again
          </button>
        }
      />
    );
  }

  if (!page) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', flex: 1 }}>
        <Spinner size={20} />
      </div>
    );
  }

  return (
    <div className="sheet runs-ledger" aria-busy={busy}>
      <div className="sheet-bar">
        <Kicker count={page.total}>Runs</Kicker>
        <label className="runs-search">
          <span className="topbar-filter-label">Ticket</span>
          <input
            type="search"
            value={parsed.ticket ?? ''}
            placeholder="Key"
            aria-label="Ticket key"
            onChange={(event) => patch({ ticket: event.target.value || null })}
          />
        </label>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">State</span>
          <select
            value={parsed.state ?? ''}
            onChange={(event) => patch({ state: event.target.value || null })}
            aria-label="State"
          >
            <option value="">All states</option>
            {STATE_ORDER.map((state) => (
              <option key={state} value={state}>
                {state}
              </option>
            ))}
          </select>
        </label>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">Status</span>
          <select
            value={parsed.status ?? ''}
            onChange={(event) => patch({ status: event.target.value || null, outcome: null })}
            aria-label="Status"
          >
            <option value="">All statuses</option>
            {RUN_STATUSES.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
        </label>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">Harness</span>
          <select
            value={parsed.harness ?? ''}
            onChange={(event) => patch({ harness: event.target.value || null })}
            aria-label="Harness"
          >
            <option value="">All harnesses</option>
            {RUN_HARNESSES.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.label}
              </option>
            ))}
          </select>
        </label>
        <label className="picker topbar-filter">
          <span className="topbar-filter-label">Outcome</span>
          <select
            value={parsed.outcome ?? ''}
            onChange={(event) => patch({ outcome: event.target.value || null })}
            aria-label="Outcome"
          >
            <option value="">All outcomes</option>
            <option value="failed">Failed</option>
            <option value="ok">Succeeded</option>
            <option value="running">Running</option>
          </select>
        </label>
        {filterActive ? (
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={() => onChangeFilter(runsFilterFrom({}))}
          >
            Clear filters
          </button>
        ) : null}
      </div>
      {error ? (
        <RefreshError
          what="The run ledger"
          error={error}
          since={loadedAt}
          now={live.tick}
          onRetry={() => void load()}
        />
      ) : null}
      <div className="sheet-body">
        {page.runs.length === 0 ? (
          <Empty title={empty.title} body={empty.body} />
        ) : (
          <>
            <div className="sheet-panel">
              <table className="table">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Ticket</th>
                    <th>State</th>
                    <th>Harness</th>
                    {showMachine ? <th>Machine</th> : null}
                    <th className="right">Duration</th>
                    <th className="right">Tokens</th>
                    <th className="right">Cost</th>
                    <th>Status</th>
                    <th>Definition</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {page.runs.map((run) => (
                    <Fragment key={`${run.lane}-${run.id}`}>
                      <AttemptRow
                        run={run}
                        showMachine={showMachine}
                        selected={run.lane === selectedRunLane && selectedRun === run.id}
                        liveTick={live.tick}
                        onOpenTicket={onOpenTicket}
                        onSelectRun={onSelectRun}
                      />
                      {run.lane === selectedRunLane && selectedRun === run.id ? (
                        <tr className="runs-transcript-row">
                          <td colSpan={showMachine ? 11 : 10}>
                            <RunTranscript lane={run.lane} runId={run.id} onClose={() => onSelectRun(null)} />
                          </td>
                        </tr>
                      ) : null}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--s3)', marginTop: 'var(--s4)' }}>
              <button
                type="button"
                className="btn btn-sm"
                disabled={page.offset === 0 || busy}
                onClick={() => setOffset(Math.max(0, page.offset - PAGE))}
              >
                Newer
              </button>
              <button
                type="button"
                className="btn btn-sm"
                disabled={page.offset + page.limit >= page.total || busy}
                onClick={() => setOffset(page.offset + PAGE)}
              >
                Older
              </button>
              <span className="sheet-note">
                {plural(page.total, 'run')} on record, attempts and sidecar jobs together. A
                retry is its own row, with its own transcript.
              </span>
            </div>

          </>
        )}
      </div>
    </div>
  );
}

function AttemptRow({
  run,
  showMachine,
  selected,
  liveTick,
  onOpenTicket,
  onSelectRun,
}: {
  run: UiAttemptRun;
  showMachine: boolean;
  selected: boolean;
  liveTick: number;
  onOpenTicket: (key: string) => void;
  onSelectRun: (id: number | null, lane?: 'attempt' | 'sidecar') => void;
}) {
  const running = run.finishedAt === null;
  const outcome = statusStyle(run.status, run.failureType);
  const duration =
    run.durationMs != null
      ? compactDuration(run.durationMs)
      : running && run.startedAt != null
        ? `${compactDuration(Math.max(0, liveTick - run.startedAt))}…`
        : run.startedAt != null && run.finishedAt != null
          ? compactDuration(run.finishedAt - run.startedAt)
          : '—';
  return (
    <tr className={selected ? 'is-selected' : undefined}>
      <td
        className="num dim runs-nums"
        title={run.startedAt != null ? absoluteTime(run.startedAt) : 'Not started'}
      >
        {run.startedAt != null ? clockStampSeconds(run.startedAt) : '—'}
      </td>
      <td>
        {run.ticketKey !== null ? <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ padding: '1px 4px' }}
          aria-label={`Open ticket ${run.ticketKey}`}
          onClick={() => { if (run.ticketKey !== null) onOpenTicket(run.ticketKey); }}
        >
          <span className="mono">{run.ticketKey}</span>
        </button> : <span className="dim">No ticket</span>}
      </td>
      <td>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {run.state !== null ? <StateChip state={run.state} /> : null}
          {run.lane === 'sidecar' ? (
            <span
              className="chip runs-sidecar"
              title={run.sidecarKind === 'creation-draft' ? 'Quick Add generates a draft before a ticket exists' : 'A sidecar job: it took no worker slot and did not move this ticket'}
            >
              {run.sidecarKind === 'creation-draft' ? 'quick add' : `sidecar ${run.sidecarKind === 'footprint' ? 'footprint' : run.sidecarKind === 'brief-cleanup' ? 'brief cleanup' : run.sidecarKind === 'text-task' ? 'text task' : 'scan'}`}
            </span>
          ) : run.ordinal > 1 ? (
            <span className="chip num" title={`Ordinal ${run.ordinal}`}>
              #{run.ordinal}
            </span>
          ) : null}
        </span>
      </td>
      <td>
        {run.harness ? (
          <VendorMark vendor={run.harness} model={run.model ?? undefined} size="xs" />
        ) : (
          <span className="dim">—</span>
        )}
        {run.reviewerName ? <div className="runs-reviewer">{run.reviewerName}</div> : null}
      </td>
      {showMachine ? (
        <td className="mono dim" style={{ fontSize: 'var(--fs-tiny)' }}>
          {run.machine ?? 'Unassigned'}
        </td>
      ) : null}
      <td className="right num runs-nums">{duration}</td>
      <td className="right num dim runs-nums" title={`${run.tokensIn} in / ${run.tokensOut} out`}>
        {tokens(run.tokensIn + run.tokensOut)}
      </td>
      <td className="right num runs-nums">{run.costUsd == null ? '—' : usd(run.costUsd)}</td>
      <td>
        <span
          style={{ color: outcome.color }}
          title={run.failureDetail ?? run.failureType ?? run.status}
        >
          {run.sidecarKind === 'creation-draft' && run.status === 'Failed' && outcome.label !== 'failed' ? `failed · ${outcome.label}` : outcome.label}
        </span>
        {run.failureDetail ? (
          <div className="runs-failure-detail" title={run.failureDetail}>
            {run.failureDetail}
          </div>
        ) : null}
      </td>
      <td>
        {run.definition ? (
          <details className="runs-def">
            <summary>{run.definition.label}</summary>
            <code className="runs-def-hash">{run.definition.contentHash}</code>
          </details>
        ) : (
          <span className="dim">—</span>
        )}
      </td>
      <td>
        {run.hasTranscript ? (
          <button
            type="button"
            className="btn btn-sm"
            aria-expanded={selected}
            aria-controls={selected ? `run-transcript-${run.id}` : undefined}
            onClick={() => onSelectRun(selected ? null : run.id, run.lane)}
          >
            {selected ? 'Hide transcript' : 'Transcript'}
          </button>
        ) : (
          <span className="dim">—</span>
        )}
      </td>
    </tr>
  );
}

/** The server serves at most this much per request. Ask for all of it: the operator wants the whole run. */
const TRANSCRIPT_WINDOW = 1024 * 1024;

function RunTranscript({ runId, lane, onClose }: { runId: number; lane: 'attempt' | 'sidecar'; onClose: () => void }) {
  const paneRef = useRef<HTMLDivElement>(null);
  const [payload, setPayload] = useState<({
    size: number;
    offset: number;
    text: string;
    truncated: boolean;
    missing: boolean;
  } & TranscriptExpiry) | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [raw, setRaw] = useState(false);
  const summariser = useTranscriptSummary(runId);

  const load = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      setPayload(await api.transcript(runId, { bytes: TRANSCRIPT_WINDOW, lane }));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    } finally {
      setBusy(false);
    }
  }, [runId, lane]);

  const { reset } = summariser;
  useEffect(() => {
    setPayload(null);
    reset();
    void load();
  }, [load, reset]);

  useEffect(() => {
    paneRef.current?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [payload]);

  // No clipping here: this is the place the operator comes to read the whole thing, and a
  // failure message cut at 160 characters ("Available models: ... gpt-…") is the one line
  // they most need in full. The live tail keeps its short lines; this viewer does not.
  const readable = payload ? extractReadableLines(payload.text, { maxLine: Infinity }) : [];
  const rawLines = payload ? payload.text.split(/\r?\n/).filter((line) => line.trim().length > 0) : [];

  return (
    <section
      id={`run-transcript-${runId}`}
      className="runs-transcript"
      ref={paneRef}
      aria-label={`Transcript for ${lane} ${runId}`}
    >
      <header className="panel-head">
        <Kicker>Transcript</Kicker>
        <span className="mono dim" style={{ fontSize: 'var(--fs-micro)' }}>
          {lane} {runId}
        </span>
        {payload && !payload.missing && payload.truncated ? (
          <span className="sheet-note" title={`${payload.size} bytes on disk`}>
            Showing the last {Math.round(TRANSCRIPT_WINDOW / 1024)} KB of a longer transcript.
          </span>
        ) : null}
        {/* A running attempt appends while this pane is open, so what is on screen is
            always a snapshot. Refresh re-fetches the same window rather than polling:
            an auto-scrolling wall of JSON is unreadable, and the operator is here to
            read. */}
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          style={{ marginLeft: 'auto' }}
          disabled={busy}
          onClick={() => void load()}
        >
          {busy ? 'Refreshing…' : 'Refresh'}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          disabled={lane === 'sidecar' || summariser.busy || payload === null || payload.missing}
          title="Summarise the tail of this transcript with a small model"
          onClick={() => void summariser.summarise()}
        >
          {summariser.label}
        </button>
        <button
          type="button"
          className="btn btn-ghost btn-sm"
          aria-pressed={raw}
          onClick={() => setRaw((value) => !value)}
        >
          {raw ? 'Readable' : 'Raw'}
        </button>
        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>
          Close
        </button>
      </header>
      <TranscriptSummary
        summary={summariser.summary}
        busy={summariser.busy}
        error={summariser.error}
        transcriptSize={payload && !payload.missing ? payload.size : null}
      />
      {error ? (
        <p style={{ color: 'var(--danger-text)' }}>{error}</p>
      ) : payload?.missing ? (
        <p className="sheet-note">{missingTranscriptNote(payload)}</p>
      ) : payload === null || busy ? (
        <div style={{ display: 'grid', placeItems: 'center', padding: 'var(--s6)' }}>
          <Spinner size={18} />
        </div>
      ) : raw ? (
        <div className="transcript scroll-y runs-transcript-body">
          {rawLines.length === 0 ? <span className="dim">Empty file.</span> : rawLines.join('\n')}
        </div>
      ) : readable.length === 0 ? (
        <p className="sheet-note">Nothing readable in this transcript. Try Raw.</p>
      ) : (
        <div className="transcript scroll-y runs-transcript-body">
          {readable.map((line, index) => (
            <div
              key={index}
              style={{
                color: line.startsWith('!')
                  ? 'var(--danger-text)'
                  : line.startsWith('$')
                    ? 'var(--accent-bright)'
                    : undefined,
              }}
            >
              {line}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
